import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { PrimaryBtn } from 'components/UI/Buttons';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useEditAdminBusinessUnitMutation } from 'services/api/adminBusinessUnits.api';
import type { KanbanStatus } from 'services/types/businessUnit';

const NAVY = '#15183A';
const YELLOW = '#F9D00A';

const ALL_STATUSES_ORDERED: KanbanStatus[] = [
  'CREATED',
  'HANDLED_BY_AI',
  'UPLOADED_TO_CRM',
  'PENDING_TO_BE_HANDLED',
  'PENDING_TO_BE_ASSIGNED',
  'ASSIGNED_TO_SALESMAN',
  'HANDLED_BY_SALESMAN',
  'CLOSED',
];

const STATUS_META: Record<KanbanStatus, { label: string; description: string; required: boolean }> = {
  CREATED: {
    label: 'Creados',
    description: 'Leads recién creados que aún no han sido atendidos.',
    required: true,
  },
  HANDLED_BY_AI: {
    label: 'Atendido IA',
    description: 'Leads en conversación temprana con la IA.',
    required: true,
  },
  UPLOADED_TO_CRM: {
    label: 'Interesado (subido al CRM)',
    description: 'Leads subidos al CRM, listos para ser contactados.',
    required: false,
  },
  PENDING_TO_BE_HANDLED: {
    label: 'Pendiente',
    description: 'Pendientes de respuesta o asignación a un ejecutivo.',
    required: false,
  },
  PENDING_TO_BE_ASSIGNED: {
    label: 'Por asignar',
    description: 'Leads en espera de asignación hasta que haya un ejecutivo en horario laboral.',
    required: false,
  },
  ASSIGNED_TO_SALESMAN: {
    label: 'Asignado',
    description: 'Asignados a un ejecutivo, en espera de ser atendidos.',
    required: false,
  },
  HANDLED_BY_SALESMAN: {
    label: 'Atendido',
    description: 'En atención activa por un ejecutivo.',
    required: false,
  },
  CLOSED: {
    label: 'Cerrado',
    description: 'Flujo terminado o lead cerrado.',
    required: true,
  },
};

const DEFAULT_CONFIG: KanbanStatus[] = ['CREATED', 'HANDLED_BY_AI', 'UPLOADED_TO_CRM', 'CLOSED'];

const chipSx = {
  fontFamily: 'Jost',
  fontWeight: 600,
  fontSize: '0.75rem',
  height: 28,
  backgroundColor: 'rgba(21, 24, 58, 0.08)',
  color: NAVY,
  '& .MuiChip-label': { px: 1.25 },
} as const;

const getCheckboxSx = (isRequired: boolean) => ({
  color: 'rgba(21, 24, 58, 0.35)',
  '&.Mui-checked': { color: YELLOW },
  '&.Mui-disabled': { color: 'rgba(21, 24, 58, 0.22)' },
  '&.Mui-disabled.Mui-checked': {
    color: isRequired ? `${NAVY} !important` : `${YELLOW} !important`,
    opacity: isRequired ? 0.55 : 0.85,
  },
});

interface StatusConfigSectionProps {
  businessUnitId: number;
  statusConfig?: KanbanStatus[] | null;
  onUpdate: () => void;
}

const StatusConfigSection: React.FC<StatusConfigSectionProps> = ({ businessUnitId, statusConfig, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<KanbanStatus[]>(statusConfig ?? DEFAULT_CONFIG);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateBusinessUnit] = useEditAdminBusinessUnitMutation();

  useEffect(() => {
    setSelectedStatuses(statusConfig ?? DEFAULT_CONFIG);
  }, [statusConfig]);

  const handleToggleStatus = (status: KanbanStatus) => {
    if (STATUS_META[status].required) return;

    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        return prev.filter((s) => s !== status);
      }
      const next = [...prev, status];
      return ALL_STATUSES_ORDERED.filter((s) => next.includes(s));
    });
  };

  const handleSave = async () => {
    setIsUpdating(true);
    try {
      await updateBusinessUnit({
        id: businessUnitId,
        businessUnit: { statusConfig: selectedStatuses },
      }).unwrap();

      onUpdate();
      setIsEditing(false);
    } catch {
      setSelectedStatuses(statusConfig ?? DEFAULT_CONFIG);
      window.alert('Error al actualizar la configuración de columnas');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setSelectedStatuses(statusConfig ?? DEFAULT_CONFIG);
    setIsEditing(false);
  };

  const originalConfig = statusConfig ?? DEFAULT_CONFIG;
  const hasChanges =
    selectedStatuses.length !== originalConfig.length || selectedStatuses.some((s, i) => s !== originalConfig[i]);

  const activeColumns = statusConfig ?? DEFAULT_CONFIG;

  return (
    <Box>
      <Typography variant="caption" sx={{ mb: 2, display: 'block', color: 'text.secondary' }}>
        Orden y visibilidad de las columnas en el embudo de leads. Las columnas fijas no se pueden quitar del embudo.
      </Typography>

      {!isEditing &&
        (activeColumns.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mb: 2 }}>
            {activeColumns.map((status) => (
              <Chip key={status} label={STATUS_META[status].label} size="small" sx={chipSx} />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            No hay columnas configuradas; se usará el orden por defecto.
          </Typography>
        ))}

      {!isEditing && (
        <Box sx={{ mb: 1 }}>
          <PrimaryBtn size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setIsEditing(true)}>
            Editar columnas
          </PrimaryBtn>
        </Box>
      )}

      {isEditing && (
        <Box>
          <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 600, color: NAVY }}>
            Estados (fijos no seleccionables)
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2, maxWidth: 400 }}>
            {ALL_STATUSES_ORDERED.map((value) => {
              const meta = STATUS_META[value];
              const isRequired = meta.required;
              const checked = selectedStatuses.includes(value);

              return (
                <Box
                  key={value}
                  sx={{
                    pl: 0.5,
                    py: 0.75,
                    mx: 1,
                    borderRadius: 1,
                    ...(!isRequired && {
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(21, 24, 58, 0.04)' },
                    }),
                  }}
                >
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start', width: '100%', m: 0 }}
                    control={
                      <Checkbox
                        checked={checked}
                        disabled={isRequired}
                        onChange={() => handleToggleStatus(value)}
                        size="small"
                        sx={{ ...getCheckboxSx(isRequired), mt: '-2px' }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: checked ? 600 : 400,
                            color: checked ? NAVY : 'rgba(21, 24, 58, 0.75)',
                            transition: 'color 0.15s, font-weight 0.15s',
                          }}
                        >
                          {meta.label}
                          {isRequired ? (
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                              (siempre visible)
                            </Typography>
                          ) : null}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: checked ? 'text.secondary' : 'rgba(21, 24, 58, 0.55)',
                            transition: 'color 0.15s',
                          }}
                        >
                          {meta.description}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              );
            })}
          </Box>

          <Typography variant="caption" sx={{ mb: 0.75, display: 'block', fontWeight: 600, color: NAVY }}>
            Orden en el embudo
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            {selectedStatuses.map((status) => (
              <Chip key={status} label={STATUS_META[status].label} size="small" sx={chipSx} />
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {}
            <PrimaryBtn
              onClick={() => void handleSave()}
              disabled={isUpdating || !hasChanges}
              variant="contained"
              size="small"
            >
              {isUpdating ? 'Guardando...' : 'Guardar cambios'}
            </PrimaryBtn>
            <PrimaryBtn onClick={handleCancelEdit} disabled={isUpdating} variant="outlined" size="small">
              Cancelar
            </PrimaryBtn>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default StatusConfigSection;
